import type { AnalyticsSummary } from '@shared/analytics';
import { useIsMobile } from '@/hooks/use-mobile';

// A responsive SVG keeps the two-series chart small and uses no remote assets.
export function TrendChart({
  rows,
  weekly,
}: {
  rows: AnalyticsSummary['trend'];
  weekly: boolean;
}) {
  const mobile = useIsMobile();
  const width = mobile ? 320 : 720,
    height = mobile ? 210 : 260,
    left = 46,
    right = 20,
    top = 16,
    bottom = 32;
  const plotWidth = width - left - right,
    plotHeight = height - top - bottom;
  const max = Math.max(1, ...rows.flatMap((row) => [row.pv, row.uv]));
  const ceiling = Math.max(1, Math.ceil(max / 4) * 4);
  const x = (index: number) =>
    left +
    (rows.length === 1
      ? plotWidth / 2
      : (index * plotWidth) / (rows.length - 1));
  const y = (value: number) => top + plotHeight * (1 - value / ceiling);
  const labelStep = Math.max(1, Math.ceil(rows.length / (mobile ? 3 : 5)));
  return (
    <svg
      className="h-full w-full"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={(weekly ? '自然周' : '每日') + ' PV 与近似 UV 趋势图'}
    >
      <title>
        {weekly ? '自然周' : '每日'} PV 与近似 UV 趋势；完整数字见下方趋势明细
      </title>
      {[0, 1, 2, 3, 4].map((step) => {
        const value = (ceiling * step) / 4;
        return (
          <g key={step}>
            <line
              x1={left}
              x2={width - right}
              y1={y(value)}
              y2={y(value)}
              stroke="#e2e8f0"
            />
            <text
              x={left - 8}
              y={y(value) + 4}
              textAnchor="end"
              fontSize="12"
              fill="#64748b"
            >
              {value.toLocaleString('zh-CN')}
            </text>
          </g>
        );
      })}
      {rows.map((row, i) =>
        i % labelStep === 0 || i === rows.length - 1 ? (
          <text
            key={row.bucket}
            x={x(i)}
            y={height - 10}
            textAnchor={
              i === 0 ? 'start' : i === rows.length - 1 ? 'end' : 'middle'
            }
            fontSize="12"
            fill="#64748b"
          >
            {row.bucket.slice(5)}
          </text>
        ) : null,
      )}
      {(['pv', 'uv'] as const).map((key) => (
        <g key={key}>
          <polyline
            points={rows.map((row, i) => `${x(i)},${y(row[key])}`).join(' ')}
            fill="none"
            stroke={key === 'pv' ? '#ea580c' : '#64748b'}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
          {rows.map((row, i) => (
            <circle
              key={row.bucket}
              cx={x(i)}
              cy={y(row[key])}
              r="3"
              fill={key === 'pv' ? '#ea580c' : '#64748b'}
            >
              <title>
                {weekly ? '周一 ' : ''}
                {row.bucket} · {key === 'pv' ? 'PV' : '近似 UV'} {row[key]}
              </title>
            </circle>
          ))}
        </g>
      ))}
    </svg>
  );
}
